// Walks recorded on the beneficiary's phone via background GPS, surfaced
// through wellnuo-backend (https://app.wellnuo.com/api/me/...). All
// timestamps are Unix millis. `endedAt: null` means a walk is currently
// in progress — the walker's phone is still streaming points.
// What opened the recording session on the phone. Mobile build 173+ lets the
// user pick a trigger mode:
//   'cadence' — >=40 spm sustained for 1 min, i.e. a recognised WALK.
//   'motion'  — any significant device motion. A superset: real walks still
//               report 'cadence', but a car trip now also produces a row.
// Absent on every walk recorded before that build, and on older backends —
// treat undefined as 'cadence' (which is what those rows actually were).
//
// This matters for aggregation: a 'motion' row is not necessarily walking, so
// folding it into walking distance / duration / avg-speed would be wrong.
export type WalkTriggerSource = 'cadence' | 'motion'

export interface WalkModel {
    id: string
    startedAt: number
    endedAt: number | null
    /** Whole recorded track, however it was travelled. */
    distanceMeters: number
    pointCount: number
    triggerSource?: WalkTriggerSource
    // Distance attribution from the server-side gait classifier (steps/min per
    // 60s window). null/undefined = not yet classified, which is NOT the same
    // as zero. The three sum to distanceMeters.
    walkingMeters?: number | null
    vehicleMeters?: number | null
    otherMeters?: number | null
    /** Server-derived: walkingMeters >= 50 m. null when unclassified. */
    isWalk?: boolean | null
    /** IANA zone where the walk started (backend v2.1.121+, from the first GPS
     *  point). null/undefined = no points or older backend — use the home zone. */
    timeZone?: string | null
}

/** Zone to show this walk's clock times in: where it happened, else the home zone. */
export function walkZone(w: WalkModel, homeZone: string): string {
    return w.timeZone || homeZone
}

/** True for sessions that represent actual walking. Undefined => legacy walk. */
export function isWalkingSession(w: WalkModel): boolean {
    return (w.triggerSource ?? 'cadence') === 'cadence'
}

/**
 * Distance to show as "walked". Falls back to the whole track for rows the
 * classifier hasn't reached — those are pre-backfill and genuinely unknown, so
 * showing the old number is more honest than showing zero.
 */
export function walkingMetersOf(w: WalkModel): number {
    return typeof w.walkingMeters === 'number' ? w.walkingMeters : (w.distanceMeters || 0)
}

/**
 * Does this session belong in the walks view at all?
 *
 * Two independent exclusions:
 *   - the session was opened by device motion, so it may be a drive entirely
 *   - the classifier found under 50 m of actual walking in it
 *
 * `isWalk === null` (unclassified) is treated as included — we don't hide data
 * on the strength of a measurement we haven't made.
 */
export function countsAsWalk(w: WalkModel): boolean {
    if (!isWalkingSession(w)) return false
    return w.isWalk !== false
}

// Per-point optional fields shipped by mobile build 114+ and persisted on
// wellnuo-backend after the §1.1.v2 walks-points migration (steps_cum,
// altitude, pressure_hpa columns). Older walks may have all three null.
export interface WalkPointModel {
    ts: number
    lat: number
    lng: number
    acc?: number | null
    steps?: number | null // cumulative TYPE_STEP_COUNTER since-last-reboot
    alt?: number | null   // GPS altitude (m above WGS84 ellipsoid)
    hpa?: number | null   // barometric pressure (hPa)
    /** How this point was travelled, from the server-side gait classifier — the
     *  same 60s windows that produce walking_distance_m. Absent on older
     *  backends; treat undefined as 'walking' so legacy tracks draw unchanged. */
    cls?: WalkPointClass
}

export type WalkPointClass = 'walking' | 'vehicle' | 'other'

/**
 * How a track segment is drawn. Walking is the subject of this report and keeps
 * the solid primary stroke; everything else is context and is de-emphasised.
 * Dash pattern carries the distinction as well as colour, so the classes remain
 * separable without relying on hue alone.
 */
export const SEGMENT_STYLE: Record<WalkPointClass, { color: string; weight: number; dashArray?: string; label: string }> = {
    walking: { color: '#2a78d6', weight: 4, label: 'Walking' },
    vehicle: { color: '#eb6834', weight: 3, dashArray: '6 6', label: 'Vehicle' },
    other: { color: '#1baf7a', weight: 3, dashArray: '2 6', label: 'Other' },
}

/** Class of the segment between two consecutive points. */
export function segmentClass(a: WalkPointModel, b: WalkPointModel): WalkPointClass {
    const ca = a.cls ?? 'walking'
    const cb = b.cls ?? 'walking'
    if (ca === cb) return ca
    // A segment straddling a transition isn't walking — don't over-claim it.
    return (ca === 'vehicle' || cb === 'vehicle') ? 'vehicle' : 'other'
}
